package com.example.mystybag.common.ui.views

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.calculateEndPadding
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.systemBars
import androidx.compose.material3.FabPosition
import androidx.compose.material3.Scaffold
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.ui.Alignment
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.extensions.Zero
import com.example.mystybag.common.ui.components.custom.StateProgressView
import com.example.mystybag.common.ui.components.custom.UIAppNavigationBar

@Composable
fun <S: Any, T: ChildViewModel<S>> UIView(
    viewModel                   : T,
    modifier                    : Modifier                  = Modifier,
    horizontalAlignment         : Alignment.Horizontal      = Alignment.CenterHorizontally,
    verticalArrangement         : Arrangement.Vertical      = Arrangement.Top,
    floatingActionButton        : @Composable () -> Unit    = {},
    floatingActionButtonPosition: FabPosition               = FabPosition.End,
    removeVerticalPadding       : Boolean                   = false,
    showTopBar                  : Boolean                   = true,
    applyBottomInsetSpacer       : Boolean                   = true,
    windowInsets                : WindowInsets              = if (removeVerticalPadding) WindowInsets.Zero else WindowInsets.systemBars,
    statusBarContent            : @Composable ((Modifier) -> Unit)? = null,
    navigationContent           : @Composable (() -> Unit)? = null,
    content                     : @Composable ColumnScope.(State<S>) -> Unit,
) {
    val baseUIState         = viewModel.baseUIState.subscribeAsState()
    val uiState             = viewModel.uiState.subscribeAsState()
    val keyboard            = LocalSoftwareKeyboardController.current
    val focusManager        = LocalFocusManager.current
    val progressProvider    = LocalProgressProvider.current

    LaunchedEffect(baseUIState.value.hideKeyboard) {
        if (baseUIState.value.hideKeyboard) {
            keyboard?.hide()
            viewModel.onKeyboardHidden()
        }
    }

    LaunchedEffect(baseUIState.value.showKeyboard) {
        if (baseUIState.value.showKeyboard) {
            keyboard?.show()
            viewModel.onKeyboardShown()
        }
    }

    LaunchedEffect(baseUIState.value.clearFocus) {
        if (baseUIState.value.clearFocus) {
            focusManager.clearFocus(true)
            viewModel.onFocusCleared()
        }
    }

    if(showTopBar)
        BoxWithToolbar(
                    viewModel,
                    modifier,
                    horizontalAlignment,
                    verticalArrangement,
                    floatingActionButton,
                    floatingActionButtonPosition,
                    removeVerticalPadding,
                    windowInsets,
                    statusBarContent,
                    navigationContent,
                    content
        )
    else
        BoxWithoutToolbar(
                    viewModel,
                    modifier,
                    horizontalAlignment,
                    verticalArrangement,
                    floatingActionButton,
                    floatingActionButtonPosition,
                    removeVerticalPadding,
                    applyBottomInsetSpacer,
                    windowInsets,
                    content
        )

}


@Composable
fun <S: Any, T: ChildViewModel<S>> BoxWithToolbar(
    viewModel                   : T,
    modifier                    : Modifier                  = Modifier,
    horizontalAlignment         : Alignment.Horizontal      = Alignment.CenterHorizontally,
    verticalArrangement         : Arrangement.Vertical      = Arrangement.Top,
    floatingActionButton        : @Composable () -> Unit    = {},
    floatingActionButtonPosition: FabPosition               = FabPosition.End,
    removeVerticalPadding       : Boolean                   = false,
    windowInsets                : WindowInsets              = if (removeVerticalPadding) WindowInsets.Zero else WindowInsets.systemBars,
    statusBarContent            : @Composable ((Modifier) -> Unit)? = null,
    navigationContent           : @Composable (() -> Unit)? = null,
    content                     : @Composable ColumnScope.(State<S>) -> Unit,
){
    val baseUIState         = viewModel.baseUIState.subscribeAsState()
    val uiState             = viewModel.uiState.subscribeAsState()
    val progressProvider    = LocalProgressProvider.current
    val layoutDirection     = LocalLayoutDirection.current

    Box(modifier = Modifier.fillMaxSize()) {
    Scaffold(
        topBar                          = {
            Column(modifier = Modifier.fillMaxWidth()) {
                statusBarContent?.invoke(
                    Modifier
                        .fillMaxWidth()
                        .height(windowInsets.asPaddingValues().calculateTopPadding())
                ) ?: run {
                    Spacer(modifier = Modifier
                        .fillMaxWidth()
                        // .background(AppTheme.current.navigationCustomColors.topNavigationBarBackground)
                        .height(windowInsets.asPaddingValues().calculateTopPadding())
                    )
                }
                UIAppNavigationBar(viewModel.navCallbacks, navigationContent)
            }
        },
        containerColor                  = Color.Transparent,
        contentColor                    = AppTheme.current.colors.onSurface,
        floatingActionButton            = floatingActionButton,
        floatingActionButtonPosition    = floatingActionButtonPosition,
        contentWindowInsets             = windowInsets
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier.fillMaxSize(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(p0)
            ) {
                Column(
                    modifier            = modifier
                        .padding(
                            PaddingValues(
                                top = it.calculateTopPadding(),
                                start = it.calculateStartPadding(layoutDirection),
                                end = it.calculateEndPadding(layoutDirection),
                                bottom = 0.dp
                            )
                        )
                        .weight(1f),
                    horizontalAlignment = horizontalAlignment,
                    verticalArrangement = verticalArrangement
                ) {
                    content(uiState)
                }

                Spacer(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding())
                    // .background(AppTheme.current.navigationCustomColors.navigationBar)
                )
            }
        }
    }

    if (progressProvider)
        StateProgressView(visible = baseUIState.value.showProgress)
}
}


@Composable
fun <S: Any, T: ChildViewModel<S>> BoxWithoutToolbar(
    viewModel                   : T,
    modifier                    : Modifier                  = Modifier,
    horizontalAlignment         : Alignment.Horizontal      = Alignment.CenterHorizontally,
    verticalArrangement         : Arrangement.Vertical      = Arrangement.Top,
    floatingActionButton        : @Composable () -> Unit    = {},
    floatingActionButtonPosition: FabPosition               = FabPosition.End,
    removeVerticalPadding       : Boolean                   = false,
    applyBottomInsetSpacer      : Boolean                   = true,
    windowInsets                : WindowInsets              = if (removeVerticalPadding) WindowInsets.Zero else WindowInsets.systemBars,
    content                     : @Composable ColumnScope.(State<S>) -> Unit,
){
    val baseUIState         = viewModel.baseUIState.subscribeAsState()
    val uiState             = viewModel.uiState.subscribeAsState()
    val progressProvider    = LocalProgressProvider.current
    val layoutDirection     = LocalLayoutDirection.current

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            containerColor                  = Color.Transparent,
            contentColor                    = AppTheme.current.colors.onSurface,
            floatingActionButton            = floatingActionButton,
            floatingActionButtonPosition    = floatingActionButtonPosition,
            contentWindowInsets             = windowInsets
        ) {
            Box(modifier = Modifier.fillMaxSize()) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(p0)
                ) {
                    Column(
                        modifier            = modifier
                            .padding(
                                PaddingValues(
                                    top = it.calculateTopPadding(),
                                    start = it.calculateStartPadding(layoutDirection),
                                    end = it.calculateEndPadding(layoutDirection),
                                    bottom = 0.dp
                                )
                            )
                            .weight(1f),
                        horizontalAlignment = horizontalAlignment,
                        verticalArrangement = verticalArrangement
                    ) {
                        content(uiState)
                    }

                    if (applyBottomInsetSpacer) {
                        Spacer(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding())
                            // .background(AppTheme.current.navigationCustomColors.navigationBar)
                        )
                    }
                }
            }
        }

        if (progressProvider)
            StateProgressView(visible = baseUIState.value.showProgress)
    }
}